/*
 * Description: Display Collapsible Web Component
 * Purpose: Displays a collapsible section with click-to-toggle functionality
 * Author: Nuvemx
 * Created: 2026
 *
 * Features:
 * - Click-to-toggle collapsible content
 * - Starts collapsed by default
 * - Configurable title and icon
 * - Public methods: toggle(), open(), close()
 * - Custom event on toggle
 *
 * Usage:
 * <display-collapsible title="Click to expand">Hidden content here.</display-collapsible>
 * <display-collapsible title="Details" icon="bx-plus">More info.</display-collapsible>
 *
 * Attributes:
 * - title: String - Button label text (required)
 * - icon: String - Boxicons icon class (default: 'bx-chevron-down')
 *
 * Events:
 * - 'collapsible-toggle': Fired when toggled, detail: { open: boolean }
 *
 */

import './display_collapsible.css';

class display_collapsible extends HTMLElement {
    constructor() {
        super();
        this._isOpen = false;
        this._onToggle = () => this.toggle();
    }

    connectedCallback() {
        if (this.dataset.rendered) return;

        const title = this.getAttribute('title') || 'Toggle';
        const icon = this.getAttribute('icon') || 'bx-chevron-down';
        const content = this.innerHTML.trim();
        const contentId = `collapsible-${Math.random().toString(36).substr(2, 9)}`;

        this.innerHTML = `
            <div class="collapsible-section mb-3">
                <button class="btn btn-outline-secondary w-100 text-start d-flex justify-content-between align-items-center collapsible-toggle-btn" type="button" aria-expanded="false" aria-controls="${contentId}">
                    <span>${title}</span>
                    <i class="bx ${icon} collapsible-icon"></i>
                </button>
                <div class="collapsible-content mt-2" id="${contentId}" style="display: none;">
                    ${content}
                </div>
            </div>`;

        this.dataset.rendered = "true";

        this._bindToggle();
    }

    _bindToggle() {
        const button = this.querySelector('.collapsible-toggle-btn');
        if (button && !button.dataset.bound) {
            button.dataset.bound = "true";
            button.addEventListener('click', this._onToggle);
        }
    }

    toggle() {
        this._isOpen = !this._isOpen;
        this._update();
        this.dispatchEvent(new CustomEvent('collapsible-toggle', {
            detail: { open: this._isOpen }
        }));
    }

    open() {
        this._isOpen = true;
        this._update();
    }

    close() {
        this._isOpen = false;
        this._update();
    }

    _update() {
        const content = this.querySelector('.collapsible-content');
        const button = this.querySelector('.collapsible-toggle-btn');
        if (content) {
            content.style.display = this._isOpen ? 'block' : 'none';
        }
        if (button) {
            button.setAttribute('aria-expanded', String(this._isOpen));
        }
    }
}

export { display_collapsible };
